import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './PersonasModule.module.css';
import { Project } from '../../types';
import { Button } from '../common/Button/Button';
import { Modal } from '../common/Modal/Modal';
import { useCrud } from '../../hooks/useCrud';
import { personasAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { PersonaDetail } from './PersonaDetail';

interface PersonasModuleProps {
  project: Project;
}

interface Persona {
  _id: string;
  name: string;
  type: 'primary' | 'secondary' | 'tertiary';
  avatar?: string;
  demographics?: {
    age?: { min?: number; max?: number };
    gender?: string;
    education?: string;
    occupation?: string;
    income?: string;
    location?: { city?: string; region?: string; country?: string };
    familyStatus?: string;
    children?: boolean;
  };
  psychographics?: {
    interests?: string[];
    values?: string[];
    lifestyle?: string;
    personality?: string;
    motivations?: string[];
    frustrations?: string[];
    goals?: string[];
    fears?: string[];
  };
  behaviors?: {
    mediaConsumption?: {
      preferredChannels?: string[];
      onlineHours?: number;
      devices?: string[];
    };
    buyingBehavior?: {
      decisionFactors?: string[];
      preferredChannels?: string[];
      researchMethods?: string[];
    };
    communicationPreference?: string;
  };
  projectRelation?: {
    awareness?: string;
    sentiment?: string;
    influence?: string;
    engagement?: string;
  };
  needs?: string[];
  painPoints?: string[];
  opportunities?: string[];
  quotes?: string[];
  scenario?: {
    dayInLife?: string;
    userJourney?: Array<{
      stage: string;
      actions: string[];
      thoughts: string[];
      emotions: string[];
      touchpoints: string[];
    }>;
  };
  notes?: string;
  isActive?: boolean;
  // CASI fields
  biography?: string;
  quote?: string;
  dailyRoutine?: string[];
  currentBehavior?: string;
  desiredBehavior?: string;
  triggers?: string[];
  momentOfTruth?: string;
  behavioralDeterminants?: Record<string, {
    description?: string;
    effect?: string;
    leverage?: string;
    source?: string;
  }>;
  top3Determinants?: { name?: string; reasoning?: string; communicationImplication?: string }[];
  interventionChecklist?: string[];
  qualityControl?: {
    stereotypeRisks?: string[];
    actionableQuestions?: string[];
  };
  sourcesAndDataQuality?: {
    projectSources?: string;
    assumptions?: string;
    knowledgeGaps?: string;
  };
  communicationPreferences?: string[];
}


export const PersonasModule: React.FC<PersonasModuleProps> = ({ project }) => {
  const { user } = useAuth();
  const { '*': subPath } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<{
    name: string;
    type: 'primary' | 'secondary' | 'tertiary';
    demographics: {
      ageRange: string;
      occupation: string;
      familyStatus: string;
    };
    psychographics: {
      interests: string;
      values: string;
      goals: string;
      frustrations: string;
    };
    behaviors: {
      preferredChannels: string;
    };
    needs: string;
    painPoints: string;
    notes: string;
  }>({
    name: '',
    type: 'primary',
    demographics: {
      ageRange: '',
      occupation: '',
      familyStatus: ''
    },
    psychographics: {
      interests: '',
      values: '',
      goals: '',
      frustrations: ''
    },
    behaviors: {
      preferredChannels: ''
    },
    needs: '',
    painPoints: '',
    notes: ''
  });

  const [fetchingAvatars, setFetchingAvatars] = useState(false);

  const {
    items: personas,
    loading,
    error,
    selectedItem,
    showCreateModal,
    showEditModal,
    canEdit,
    canDelete,
    setShowCreateModal,
    openEditModal,
    closeModals,
    createItem,
    updateItem,
    deleteItem,
    refreshItems
  } = useCrud<Persona>({
    fetchFn: personasAPI.getByProject,
    createFn: personasAPI.create,
    updateFn: personasAPI.update,
    deleteFn: personasAPI.delete,
    projectId: project.id
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Parse age range
    const ageRange = formData.demographics.ageRange;
    let ageMin, ageMax;
    if (ageRange) {
      const match = ageRange.match(/(\d+)[^\d]+(\d+)/);
      if (match) {
        ageMin = parseInt(match[1]);
        ageMax = parseInt(match[2]);
      }
    }

    const dataToSubmit: any = {
      project: project.id,
      name: formData.name,
      type: formData.type,
      psychographics: {
        interests: formData.psychographics.interests.split(',').map(s => s.trim()).filter(Boolean),
        values: formData.psychographics.values.split(',').map(s => s.trim()).filter(Boolean),
        goals: formData.psychographics.goals.split(',').map(s => s.trim()).filter(Boolean),
        frustrations: formData.psychographics.frustrations.split(',').map(s => s.trim()).filter(Boolean)
      },
      behaviors: {
        mediaConsumption: {
          preferredChannels: formData.behaviors.preferredChannels.split(',').map(s => s.trim()).filter(Boolean)
        }
      },
      needs: formData.needs.split(',').map(s => s.trim()).filter(Boolean),
      painPoints: formData.painPoints.split(',').map(s => s.trim()).filter(Boolean)
    };

    // Only add demographics fields if they have values
    if (ageMin && ageMax) {
      dataToSubmit.demographics = dataToSubmit.demographics || {};
      dataToSubmit.demographics.age = { min: ageMin, max: ageMax };
    }
    if (formData.demographics.occupation) {
      dataToSubmit.demographics = dataToSubmit.demographics || {};
      dataToSubmit.demographics.occupation = formData.demographics.occupation;
    }
    if (formData.demographics.familyStatus) {
      dataToSubmit.demographics = dataToSubmit.demographics || {};
      dataToSubmit.demographics.familyStatus = formData.demographics.familyStatus;
    }
    if (formData.notes) {
      dataToSubmit.notes = formData.notes;
    }

    if (selectedItem) {
      await updateItem(selectedItem._id, dataToSubmit);
    } else {
      await createItem(dataToSubmit);
    }

    setFormData({
      name: '',
      type: 'primary',
      demographics: {
        ageRange: '',
        occupation: '',
        familyStatus: ''
      },
      psychographics: {
        interests: '',
        values: '',
        goals: '',
        frustrations: ''
      },
      behaviors: {
        preferredChannels: ''
      },
      needs: '',
      painPoints: '',
      notes: ''
    });
  };

  const handleEdit = (persona: Persona) => {
    const ageRange = persona.demographics?.age
      ? `${persona.demographics.age.min || ''}-${persona.demographics.age.max || ''}`
      : '';

    setFormData({
      name: persona.name,
      type: persona.type,
      demographics: {
        ageRange: ageRange,
        occupation: persona.demographics?.occupation || '',
        familyStatus: persona.demographics?.familyStatus || ''
      },
      psychographics: {
        interests: persona.psychographics?.interests?.join(', ') || '',
        values: persona.psychographics?.values?.join(', ') || '',
        goals: persona.psychographics?.goals?.join(', ') || '',
        frustrations: persona.psychographics?.frustrations?.join(', ') || ''
      },
      behaviors: {
        preferredChannels: persona.behaviors?.mediaConsumption?.preferredChannels?.join(', ') || ''
      },
      needs: persona.needs?.join(', ') || '',
      painPoints: persona.painPoints?.join(', ') || '',
      notes: persona.notes || ''
    });
    openEditModal(persona);
  };

  const handleDuplicate = async (persona: Persona) => {
    try {
      await personasAPI.duplicate(persona._id);
      await refreshItems();
    } catch (error) {
      console.error('Failed to duplicate persona:', error);
    }
  };

  const handleFetchAvatars = async () => {
    setFetchingAvatars(true);
    try {
      const result = await personasAPI.fetchAvatars(project.id);
      console.log('Avatar fetch result:', result);
      await refreshItems();
    } catch (error) {
      console.error('Failed to fetch avatars:', error);
    } finally {
      setFetchingAvatars(false);
    }
  };

  const handleRegenerateAvatars = async () => {
    setFetchingAvatars(true);
    try {
      const result = await personasAPI.regenerateAvatars(project.id);
      console.log('Avatar regenerate result:', result);
      await refreshItems();
    } catch (error) {
      console.error('Failed to regenerate avatars:', error);
    } finally {
      setFetchingAvatars(false);
    }
  };

  const hasPersonasWithoutAvatar = personas.some((p: Persona) => !p.avatar);
  const hasPersonasWithAvatar = personas.some((p: Persona) => !!p.avatar);

  const getPersonaIcon = (type: string) => {
    switch (type) {
      case 'primary':
      case 'residents':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        );
      case 'secondary':
      case 'commuters':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
          </svg>
        );
      case 'tertiary':
      case 'business':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        );
      default:
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        );
    }
  };


  if (loading) {
    return (
      <div className={styles.personasModule}>
        <div className={styles.loading}>Loading personas...</div>
      </div>
    );
  }

  const displayPersonas: Persona[] = personas;

  // Show detail view when a persona is selected via URL
  const viewingPersona = subPath ? personas.find(p => p._id === subPath) : null;
  if (subPath && viewingPersona) {
    return (
      <div className={styles.personasModule}>
        <PersonaDetail
          persona={viewingPersona as any}
          onClose={() => navigate(`/project/${project.id}/personas`)}
          canEdit={canEdit}
          onUpdate={() => refreshItems()}
        />
      </div>
    );
  }

  return (
    <div className={styles.personasModule}>
      <div className={styles.header}>
        <h1>Stakeholder persona's</h1>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {hasPersonasWithoutAvatar && (
            <Button
              variant="outline"
              onClick={handleFetchAvatars}
              disabled={fetchingAvatars}
            >
              {fetchingAvatars ? 'Ophalen...' : "Foto's ophalen"}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
            >
              + Nieuwe Persona
            </Button>
          )}
        </div>
      </div>

      {displayPersonas.length === 0 ? (
        <div className={styles.emptyState}>
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <h3>Nog geen persona's aangemaakt</h3>
          <p>Maak persona's aan om uw stakeholdergroepen te definiëren en hun behoeften beter te begrijpen.</p>
          {canEdit && (
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              + Eerste persona toevoegen
            </Button>
          )}
        </div>
      ) : (
        <div className={styles.personaGrid}>
          {displayPersonas.map((persona) => {
          const id = persona._id;
          const name = persona.name || 'Naamloze Persona';
          const concerns = persona.painPoints?.join(', ') || persona.psychographics?.frustrations?.join(', ') || '';
          const channels = persona.behaviors?.mediaConsumption?.preferredChannels?.join(', ') || persona.communicationPreferences?.join(', ') || '';
          const characteristics = persona.notes || persona.biography || [persona.demographics?.occupation, persona.demographics?.familyStatus].filter(Boolean).join(', ') || persona.type || '';

          return (
            <div
              key={id}
              className={`${styles.personaCard} ${persona.isActive === false ? styles.inactive : ''}`}
              onClick={() => navigate(`/project/${project.id}/personas/${persona._id}`)}
              style={{ cursor: 'pointer' }}
            >
              {canEdit && (
                <button
                  className={styles.personaEditBtn}
                  title="Bewerken"
                  onClick={(e) => { e.stopPropagation(); handleEdit(persona); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              )}
              <div className={styles.personaAvatar}>
                {persona.avatar ? (
                  <img src={persona.avatar} alt={name} className={styles.avatarImg} />
                ) : (
                  getPersonaIcon(persona.type)
                )}
              </div>
              <div className={styles.personaCardContent}>
                <h4>{name}</h4>
                {persona.demographics?.occupation && (
                  <span className={styles.personaOccupation}>{persona.demographics.occupation}</span>
                )}
                {persona.quote && (
                  <p className={styles.personaQuote}>
                    "{persona.quote.length > 100 ? persona.quote.substring(0, 100) + '...' : persona.quote}"
                  </p>
                )}
                <div className={styles.personaTags}>
                  {persona.top3Determinants && persona.top3Determinants.length > 0 && (() => {
                    // Filter 'zelfbeeld' uit de weergave — toon de eerste relevante bepaler
                    const relevantDet = (persona as any).top3Determinants.find(
                      (d: any) => d?.name && d.name.toLowerCase() !== 'zelfbeeld'
                    );
                    return relevantDet ? (
                      <span className={styles.personaTag}>
                        {relevantDet.name}
                      </span>
                    ) : null;
                  })()}
                  <span
                    className={styles.personaTypeBadge}
                    title={
                      persona.type === 'primary' ? 'Direct geraakt — woont, werkt of verblijft in het invloedsgebied' :
                      persona.type === 'secondary' ? 'Indirect geraakt — ondervindt gevolgen via omleiding, waardedaling of veranderd voorzieningenniveau' :
                      'Observerend/beleidsmatig — volgt het project vanuit bestuurlijke, politieke of maatschappelijke rol'
                    }
                  >
                    {persona.type === 'primary' ? 'Primair' : persona.type === 'secondary' ? 'Secundair' : 'Tertiair'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}

      <div className={styles.theoryInfo}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div>
          <p className={styles.theoryIntro}>
            De persona's in OmgevingsCompas zijn opgebouwd aan de hand van wetenschappelijk onderbouwde modellen voor gedragsanalyse en stakeholdercommunicatie:
          </p>
          <ul className={styles.theoryList}>
            <li>
              <strong>CASI-model</strong> (Communicatie Activatie Strategie Instrument) — Communicatiestrategisch instrument waarmee je via een gestructureerde analyse bepaalt welke communicatie-interventies het meest effectief zijn om gewenst gedrag te beïnvloeden.
              {' '}<a href="https://www.communicatierijk.nl/vakkennis/gedrag-casi-en-campagnes/casi" target="_blank" rel="noopener noreferrer">Meer over CASI</a>
            </li>
            <li>
              <strong>COM-B model</strong> (Capability, Opportunity, Motivation → Behaviour) — Identificeert gedragsdeterminanten: wat kunnen, willen en mogen stakeholders? Basis voor de gedragsdeterminanten in elke persona.
              {' '}<a href="https://implementationscience.biomedcentral.com/articles/10.1186/1748-5908-6-42" target="_blank" rel="noopener noreferrer">Meer over COM-B</a>
            </li>
            <li>
              <strong>Mutual Gains Approach (MGA)</strong> — Onderhandelingsmethode gericht op gezamenlijke winst: zoek naar gedeelde belangen en creatieve oplossingen in plaats van positiegebonden onderhandelen.
              {' '}<a href="https://www.pon.harvard.edu/tag/mutual-gain/" target="_blank" rel="noopener noreferrer">Meer over Mutual Gains</a>
            </li>
            <li>
              <strong>SOM-model</strong> (Strategisch OmgevingsManagement) — Stakeholdermanagement op basis van belangen, posities en invloed. Structureert de relatie tussen project en omgeving.
            </li>
          </ul>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || showEditModal}
        onClose={closeModals}
        title={selectedItem ? 'Persona Bewerken' : 'Nieuwe Persona'}
        footer={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={closeModals}>
              Annuleren
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {selectedItem ? 'Opslaan' : 'Toevoegen'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Basisgegevens
            </h3>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>
                  Naam <span className={styles.required}>*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className={styles.input}
                  placeholder="Bijv. Direct Omwonenden"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="type" className={styles.label}>
                  Type <span className={styles.required}>*</span>
                </label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'primary' | 'secondary' | 'tertiary' })}
                  className={styles.select}
                >
                  <option value="primary">Primair - Directe stakeholders</option>
                  <option value="secondary">Secundair - Indirecte betrokkenen</option>
                  <option value="tertiary">Tertiair - Overige belanghebbenden</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="notes" className={styles.label}>Notities</label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className={styles.textarea}
                placeholder="Aanvullende informatie over deze persona..."
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
              Psychografisch Profiel
            </h3>

            <div className={styles.formGroup}>
              <label htmlFor="interests" className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                Interesses
              </label>
              <input
                id="interests"
                type="text"
                value={formData.psychographics.interests}
                onChange={(e) => setFormData({
                  ...formData,
                  psychographics: { ...formData.psychographics, interests: e.target.value }
                })}
                placeholder="Bijv. technologie, duurzaamheid, sport"
                className={styles.input}
              />
              <span className={styles.helpText}>Gescheiden door komma's</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="painPoints" className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Zorgen & Pijnpunten
              </label>
              <input
                id="painPoints"
                type="text"
                value={formData.painPoints}
                onChange={(e) => setFormData({ ...formData, painPoints: e.target.value })}
                placeholder="Geluidsoverlast, trillingen, waardedaling"
                className={styles.input}
              />
              <span className={styles.helpText}>Gescheiden door komma's</span>
            </div>

          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Communicatie & Behoeften
            </h3>

            <div className={styles.formGroup}>
              <label htmlFor="preferredChannels" className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Voorkeur Communicatiekanalen
              </label>
              <input
                id="preferredChannels"
                type="text"
                value={formData.behaviors.preferredChannels}
                onChange={(e) => setFormData({
                  ...formData,
                  behaviors: { ...formData.behaviors, preferredChannels: e.target.value }
                })}
                placeholder="Persoonlijke brief, keukentafelgesprekken, email"
                className={styles.input}
              />
              <span className={styles.helpText}>Gescheiden door komma's</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="needs" className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
                Behoeften
              </label>
              <input
                id="needs"
                type="text"
                value={formData.needs}
                onChange={(e) => setFormData({ ...formData, needs: e.target.value })}
                placeholder="Transparantie, duidelijkheid, inspraak"
                className={styles.input}
              />
              <span className={styles.helpText}>Gescheiden door komma's</span>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                <rect x="2" y="9" width="6" height="12" />
                <circle cx="5" cy="5" r="3" />
              </svg>
              Demografische Gegevens <span className={styles.optional}>(Optioneel)</span>
            </h3>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="ageRange" className={styles.label}>Leeftijdscategorie</label>
                <input
                  id="ageRange"
                  type="text"
                  value={formData.demographics.ageRange}
                  onChange={(e) => setFormData({
                    ...formData,
                    demographics: { ...formData.demographics, ageRange: e.target.value }
                  })}
                  placeholder="Bijv. 25-45 jaar"
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="occupation" className={styles.label}>Beroep/Sector</label>
                <input
                  id="occupation"
                  type="text"
                  value={formData.demographics.occupation}
                  onChange={(e) => setFormData({
                    ...formData,
                    demographics: { ...formData.demographics, occupation: e.target.value }
                  })}
                  placeholder="Bijv. IT sector, Retail"
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="familyStatus" className={styles.label}>Gezinssituatie</label>
              <select
                id="familyStatus"
                value={formData.demographics.familyStatus}
                onChange={(e) => setFormData({
                  ...formData,
                  demographics: { ...formData.demographics, familyStatus: e.target.value }
                })}
                className={styles.select}
              >
                <option value="">-- Selecteer --</option>
                <option value="single">Alleenstaand</option>
                <option value="married">Getrouwd</option>
                <option value="divorced">Gescheiden</option>
                <option value="widowed">Weduwe/Weduwnaar</option>
                <option value="partnered">Samenwonend</option>
              </select>
            </div>
          </div>
        </form>
      </Modal>

    </div>
  );
};